import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { DCard } from '../components/MarketplaceAtoms.js';
import { AdminAlertCount } from '../../lib/adminMenuBadges.js';
import { processSocialQueue } from '../../lib/socialPublishService.js';
import { fetchSocialQueuePreviews, queuePreviewKey } from '../../lib/socialQueuePreview.js';
import { SOCIAL_CONTENT_TYPES, buildPublicContentUrl } from '../../lib/socialPublishKit.js';

const STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'Procesando',
  success: 'Enviado',
  failed: 'Error',
  skipped: 'Omitido',
};

const ACTIVE_STATUSES = ['pending', 'processing', 'failed'];
const HISTORY_STATUSES = ['success', 'skipped'];

const STATUS_VIEWS = [
  { id: 'active', label: 'Por hacer' },
  { id: 'failed', label: 'Solo errores' },
  { id: 'success', label: 'Enviados' },
  { id: 'all', label: 'Todo' },
];

const btnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnDanger = {
  ...btnMini,
  border: '0.5px solid rgba(255,100,100,0.35)',
  color: C.danger,
  background: 'rgba(255,100,100,0.08)',
};

function formatDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

const CONTENT_TYPE_FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'listing', label: 'Mercado' },
  { id: 'auction', label: 'Subastas' },
  { id: 'noticia', label: 'Noticias' },
  { id: 'comunidad', label: 'Comunidad' },
];

function summarizeQueueProcessResults(results) {
  if (!results?.length) {
    return 'No había filas pendientes ni con error para procesar.';
  }
  const ok = results.filter(r => r.status === 'success').length;
  const fail = results.filter(r => r.status === 'failed').length;
  const parts = [];
  if (ok) parts.push(`${ok} enviada${ok === 1 ? '' : 's'}`);
  if (fail) parts.push(`${fail} error`);
  let msg = `Procesadas ${results.length} fila${results.length === 1 ? '' : 's'} (${parts.join(', ') || 'sin cambio'}).`;
  if (ok && !fail) {
    msg += ' Puedes limpiar los enviados con «Quitar enviados».';
  }
  const firstErr = results.find(r => r.error)?.error;
  if (firstErr && fail) {
    msg += ` ${String(firstErr).slice(0, 160)}`;
  }
  return msg;
}

function applyStatusViewFilter(query, statusView) {
  if (statusView === 'active') return query.in('status', ACTIVE_STATUSES);
  if (statusView === 'failed') return query.eq('status', 'failed');
  if (statusView === 'success') return query.in('status', HISTORY_STATUSES);
  return query;
}

/** Cola `social_publish_queue` + últimas publicaciones (solo admin, RLS). */
export default function AdminSocialQueuePanel({ onError, onSuccess, onOpenSocialKit, onBadgesRefresh, menuBadges }) {
  const [rows, setRows] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queueBusy, setQueueBusy] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [statusView, setStatusView] = useState('active');
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [queueTotals, setQueueTotals] = useState({
    pending: 0,
    processing: 0,
    failed: 0,
    success: 0,
    skipped: 0,
  });
  const [previews, setPreviews] = useState(() => new Map());
  const [previewModal, setPreviewModal] = useState(null);
  const loadReq = useRef(0);

  const fetchQueueTotals = useCallback(async () => {
    const statuses = ['pending', 'processing', 'failed', 'success', 'skipped'];
    const entries = await Promise.all(
      statuses.map(async status => {
        const { count, error } = await supabase
          .from('social_publish_queue')
          .select('id', { count: 'exact', head: true })
          .eq('status', status);
        return [status, error ? 0 : Math.max(0, Number(count) || 0)];
      }),
    );
    return Object.fromEntries(entries);
  }, []);

  const load = useCallback(async () => {
    const rq = ++loadReq.current;
    setLoading(true);
    onError?.('');
    try {
      let q = supabase
        .from('social_publish_queue')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(80);
      q = applyStatusViewFilter(q, statusView);
      if (contentTypeFilter !== 'all') q = q.eq('content_type', contentTypeFilter);
      const { data, error } = await q;
      if (rq !== loadReq.current) return;
      if (error) throw error;
      const queueRows = data ?? [];
      setRows(queueRows);
      fetchSocialQueuePreviews(queueRows).then(pm => {
        if (rq === loadReq.current) setPreviews(pm);
      });

      const { data: pubRows, error: pubErr } = await supabase
        .from('social_publications')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(40);
      if (rq !== loadReq.current) return;
      if (pubErr) throw pubErr;
      setPubs(pubRows ?? []);

      const totals = await fetchQueueTotals();
      if (rq !== loadReq.current) return;
      setQueueTotals(totals);
    } catch (e) {
      if (rq !== loadReq.current) return;
      onError?.(e?.message || 'No se pudo cargar la cola de redes.');
      setRows([]);
      setPubs([]);
    } finally {
      if (rq === loadReq.current) setLoading(false);
    }
  }, [onError, statusView, contentTypeFilter, fetchQueueTotals]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = queueTotals.pending + queueTotals.processing;
  const failedCount = queueTotals.failed;
  const activeCount = pendingCount + failedCount;
  const historyCount = queueTotals.success + queueTotals.skipped;
  const menuBadgeCount = Math.max(0, Number(menuBadges?.redes_cola) || activeCount);
  const processBatch = Math.min(10, Math.max(activeCount, 1));

  const handleProcessQueue = async () => {
    if (activeCount === 0) {
      onError?.('No hay filas pendientes ni con error.');
      return;
    }
    setQueueBusy(true);
    onError?.('');
    onSuccess?.('');
    try {
      const res = await processSocialQueue(processBatch);
      if (!res.ok) {
        onError?.(res.error || 'No se procesó la cola.');
        return;
      }
      await load();
      onBadgesRefresh?.();
      const summary = summarizeQueueProcessResults(res.results);
      const hasFailures = (res.results ?? []).some(r => r.status === 'failed');
      if (hasFailures) {
        onError?.(summary);
      } else {
        onSuccess?.(summary);
        if ((res.results ?? []).some(r => r.status === 'success')) {
          setStatusView('active');
        }
      }
    } catch (e) {
      onError?.(e?.message || 'Error al procesar cola.');
    } finally {
      setQueueBusy(false);
    }
  };

  const handleClearHistory = async () => {
    onError?.('');
    onSuccess?.('');
    try {
      const n = historyCount;
      if (!n) {
        onError?.('No hay filas enviadas u omitidas que quitar.');
        return;
      }
      if (
        !window.confirm(
          `¿Quitar ${n} fila${n === 1 ? '' : 's'} ya enviada${n === 1 ? '' : 's'} u omitida${n === 1 ? '' : 's'} de la cola?\n\nNo borra el contenido en la web ni los posts en Telegram; solo limpia el historial de esta tabla.`,
        )
      ) {
        return;
      }
      setClearBusy(true);
      const { error } = await supabase
        .from('social_publish_queue')
        .delete()
        .in('status', HISTORY_STATUSES);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
      onSuccess?.(`Historial limpiado: ${n} fila${n === 1 ? '' : 's'} eliminada${n === 1 ? '' : 's'}.`);
    } catch (e) {
      onError?.(e?.message || 'No se pudo limpiar el historial.');
    } finally {
      setClearBusy(false);
    }
  };

  const handleSkipRow = async row => {
    if (!row?.id || !ACTIVE_STATUSES.includes(row.status)) return;
    onError?.('');
    onSuccess?.('');
    setRowBusyId(row.id);
    try {
      const { error } = await supabase
        .from('social_publish_queue')
        .update({
          status: 'skipped',
          last_error: 'omitido_manual',
          processed_at: new Date().toISOString(),
        })
        .eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
      onSuccess?.('Fila omitida; no se volverá a procesar.');
    } catch (e) {
      onError?.(e?.message || 'No se pudo omitir la fila.');
    } finally {
      setRowBusyId(null);
    }
  };

  const handleDeleteRow = async row => {
    if (!row?.id) return;
    onError?.('');
    onSuccess?.('');
    if (!window.confirm('¿Eliminar esta fila de la cola?')) return;
    setRowBusyId(row.id);
    try {
      const { error } = await supabase.from('social_publish_queue').delete().eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
      onSuccess?.('Fila eliminada de la cola.');
    } catch (e) {
      onError?.(e?.message || 'No se pudo eliminar la fila.');
    } finally {
      setRowBusyId(null);
    }
  };

  const statusCountForView = viewId => {
    if (viewId === 'active') return activeCount;
    if (viewId === 'failed') return failedCount;
    if (viewId === 'success') return historyCount;
    return (
      queueTotals.pending +
      queueTotals.processing +
      queueTotals.failed +
      queueTotals.success +
      queueTotals.skipped
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DCard>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: C.gold, fontSize: 14, flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            Cola de publicación en redes
            {menuBadgeCount > 0 ? <AdminAlertCount count={menuBadgeCount} /> : null}
          </h3>
          <button type="button" style={btnMini} disabled={loading || clearBusy} onClick={() => load()}>
            Actualizar
          </button>
          <button
            type="button"
            style={btnMini}
            disabled={clearBusy || loading || historyCount === 0}
            onClick={handleClearHistory}
          >
            {clearBusy ? 'Limpiando…' : `Quitar enviados (${historyCount})`}
          </button>
          <button
            type="button"
            style={btnMini}
            disabled={queueBusy || loading || activeCount === 0}
            onClick={handleProcessQueue}
          >
            {queueBusy
              ? 'Procesando…'
              : activeCount === 0
                ? 'Nada pendiente'
                : `Procesar (${Math.min(10, activeCount)})`}
          </button>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
          <strong>Noticias y Comunidad:</strong> Telegram se envía al publicar o aprobar (directo). La cola guarda
          reintentos si falla, listings/subastas y pruebas antiguas. Lo ya enviado queda como historial hasta que pulses{' '}
          <strong>Quitar enviados</strong>. Cron opcional: <code style={{ fontSize: 10 }}>social-publish-process-queue</code>.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {CONTENT_TYPE_FILTERS.map(ct => (
            <button
              key={ct.id}
              type="button"
              style={{
                ...btnMini,
                background: contentTypeFilter === ct.id ? 'rgba(120,160,255,0.18)' : btnMini.background,
              }}
              onClick={() => setContentTypeFilter(ct.id)}
            >
              {ct.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {STATUS_VIEWS.map(view => {
            const n = statusCountForView(view.id);
            return (
              <button
                key={view.id}
                type="button"
                style={{
                  ...btnMini,
                  background: statusView === view.id ? 'rgba(200,168,75,0.22)' : btnMini.background,
                }}
                onClick={() => setStatusView(view.id)}
              >
                {view.label}
                {n > 0 ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
          Por hacer: {activeCount} ({queueTotals.pending} pendiente{queueTotals.pending === 1 ? '' : 's'}
          {queueTotals.processing > 0 ? `, ${queueTotals.processing} procesando` : ''}
          {failedCount > 0 ? `, ${failedCount} error${failedCount === 1 ? '' : 'es'}` : ''}) · Historial:{' '}
          {historyCount} · Mostrando {rows.length} fila{rows.length === 1 ? '' : 's'}
        </div>
        {loading ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Cargando…</p>
        ) : rows.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
            {statusView === 'active'
              ? 'Cola vacía: no hay pendientes ni errores.'
              : 'Sin filas en este filtro.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: 'rgba(255,255,255,0.45)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Estado</th>
                  <th style={{ padding: '6px 8px' }}>Tipo</th>
                  <th style={{ padding: '6px 8px' }}>Qué es</th>
                  <th style={{ padding: '6px 8px' }}>Redes</th>
                  <th style={{ padding: '6px 8px' }}>Intentos</th>
                  <th style={{ padding: '6px 8px' }}>Creado</th>
                  <th style={{ padding: '6px 8px' }}>Notas</th>
                  <th style={{ padding: '6px 8px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const typeLabel = SOCIAL_CONTENT_TYPES[row.content_type]?.label ?? row.content_type;
                  const url = buildPublicContentUrl(row.content_type, row.content_id);
                  const preview = previews.get(queuePreviewKey(row.content_type, row.content_id));
                  const rowBusy = rowBusyId === row.id;
                  const canSkip = ACTIVE_STATUSES.includes(row.status);
                  const note =
                    row.status === 'success'
                      ? row.last_error && !/^sent_/i.test(String(row.last_error))
                        ? row.last_error
                        : 'Enviado a Telegram'
                      : row.status === 'skipped'
                        ? row.last_error === 'omitido_manual'
                          ? 'Omitido por admin'
                          : row.last_error || 'Omitido'
                        : row.last_error || '—';
                  return (
                    <tr key={row.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '8px', color: row.status === 'failed' ? C.danger : C.gold }}>
                        {STATUS_LABELS[row.status] ?? row.status}
                      </td>
                      <td style={{ padding: '8px' }}>{typeLabel}</td>
                      <td style={{ padding: '8px', maxWidth: 220 }}>
                        <div
                          style={{
                            color: preview?.missing ? C.danger : 'rgba(255,255,255,0.9)',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={preview?.title || ''}
                        >
                          {preview?.title || 'Cargando…'}
                        </div>
                        {preview?.subtitle ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: 'rgba(255,255,255,0.4)',
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={preview.subtitle}
                          >
                            {preview.subtitle}
                          </div>
                        ) : null}
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
                          {row.source} · {String(row.content_id).slice(0, 8)}…
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>{(row.target_networks || []).join(', ') || '—'}</td>
                      <td style={{ padding: '8px' }}>{row.attempts ?? 0}</td>
                      <td style={{ padding: '8px' }}>{formatDt(row.created_at)}</td>
                      <td
                        style={{
                          padding: '8px',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: 'rgba(255,255,255,0.45)',
                        }}
                        title={note}
                      >
                        {note}
                      </td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          style={{ ...btnMini, marginRight: 4 }}
                          disabled={rowBusy}
                          onClick={() => setPreviewModal({ row, preview: preview ?? null, url, typeLabel })}
                        >
                          Vista
                        </button>
                        {onOpenSocialKit ? (
                          <button
                            type="button"
                            style={{ ...btnMini, marginRight: 4 }}
                            disabled={rowBusy}
                            onClick={() =>
                              onOpenSocialKit({
                                contentType: row.content_type,
                                contentId: row.content_id,
                                titleHint: preview?.title || typeLabel,
                              })
                            }
                          >
                            Kit
                          </button>
                        ) : null}
                        {canSkip ? (
                          <button
                            type="button"
                            style={{ ...btnMini, marginRight: 4 }}
                            disabled={rowBusy}
                            onClick={() => handleSkipRow(row)}
                          >
                            Omitir
                          </button>
                        ) : null}
                        <button
                          type="button"
                          style={btnDanger}
                          disabled={rowBusy}
                          onClick={() => handleDeleteRow(row)}
                        >
                          {rowBusy ? '…' : 'Quitar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DCard>

      {previewModal ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 12000,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setPreviewModal(null)}
        >
          <div
            style={{
              width: 'min(440px, 100%)',
              maxHeight: 'min(88vh, 640px)',
              overflow: 'auto',
              background: 'linear-gradient(160deg, #1a1a22 0%, #121218 100%)',
              border: '1px solid rgba(200,168,75,0.25)',
              borderRadius: 14,
              padding: 18,
              boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' }}>
                  Vista rápida · {previewModal.typeLabel}
                </div>
                <h4 style={{ margin: '4px 0 0', color: C.gold, fontSize: 15, lineHeight: 1.35 }}>
                  {previewModal.preview?.title || 'Sin título'}
                </h4>
              </div>
              <button type="button" style={btnMini} onClick={() => setPreviewModal(null)}>
                Cerrar
              </button>
            </div>
            {previewModal.preview?.imageUrl ? (
              <img
                src={previewModal.preview.imageUrl}
                alt=""
                style={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 10,
                  marginBottom: 12,
                  background: 'rgba(255,255,255,0.05)',
                }}
              />
            ) : null}
            {previewModal.preview?.subtitle ? (
              <p style={{ margin: '0 0 10px', fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                {previewModal.preview.subtitle}
              </p>
            ) : null}
            {previewModal.preview?.extracto ? (
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {previewModal.preview.extracto}
              </p>
            ) : null}
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, marginBottom: 12 }}>
              <div>
                Estado cola: <strong style={{ color: C.gold }}>{STATUS_LABELS[previewModal.row.status] ?? previewModal.row.status}</strong>
              </div>
              <div>Redes: {(previewModal.row.target_networks || []).join(', ') || '—'}</div>
              <div>Origen: {previewModal.row.source}</div>
              <div>Creado: {formatDt(previewModal.row.created_at)}</div>
              {previewModal.row.last_error ? <div>Nota: {previewModal.row.last_error}</div> : null}
            </div>
            <a
              href={previewModal.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...btnMini, display: 'inline-block', textDecoration: 'none' }}
            >
              Abrir en la web
            </a>
          </div>
        </div>
      ) : null}

      <DCard>
        <h3 style={{ margin: '0 0 12px', color: C.gold, fontSize: 14 }}>Publicaciones registradas</h3>
        <p style={{ margin: '0 0 10px', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
          URLs devueltas por n8n (si el workflow las envía). Telegram a menudo no guarda URL aquí aunque el mensaje se haya enviado.
        </p>
        {pubs.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Aún no hay URLs en el registro.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: 'rgba(255,255,255,0.45)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Red</th>
                  <th style={{ padding: '6px 8px' }}>Tipo</th>
                  <th style={{ padding: '6px 8px' }}>URL post</th>
                  <th style={{ padding: '6px 8px' }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {pubs.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ padding: '8px' }}>{p.network}</td>
                    <td style={{ padding: '8px' }}>{p.content_type}</td>
                    <td style={{ padding: '8px' }}>
                      <a href={p.post_url} target="_blank" rel="noopener noreferrer" style={{ color: C.goldLight }}>
                        {p.post_url.length > 48 ? `${p.post_url.slice(0, 48)}…` : p.post_url}
                      </a>
                    </td>
                    <td style={{ padding: '8px' }}>{formatDt(p.published_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DCard>
    </div>
  );
}
